import React, { useState, useEffect } from 'react';
import { Container, IconButton, Box, Button, Modal, Card, Typography, TextField, Input, Checkbox, FormControlLabel } from '@mui/material';
import DataTable from '../components/DataTable';
import PageTitle from '../components/PageTitle';
import { useConfirm } from '../assets/contexts/confirmContext';
import { useNotification } from '../assets/contexts/notificationContext';
import Loader from '../components/loader';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ACTION_ICON_COLORS } from '../constants/iconActionColors';
import * as endPoints from '../assets/js/endpoints';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { INTRANET_PAGE_CONTAINER_SX } from '../constants/layout';

const ManageServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    hora_inicio: '',
    hora_fin: '',
    duration: '',
    active: true,
  });

  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showNotification } = useNotification();

  const fetchServices = async () => {
    try {
      const data = await endPoints.getAllServices();
      setServices(Array.isArray(data) ? data : data.data);
    } catch (_err) {
      showNotification('Error al obtener servicios', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleDelete = async id => {
    const ok = await confirm('¿Eliminar servicio?');
    if (!ok) return;
    try {
      await endPoints.deleteService(id);
      fetchServices();
      showNotification('Servicio eliminado', 'success');
    } catch (_err) {
      showNotification('Error eliminando servicio', 'error');
    }
  };

  const handleEdit = async id => {
    setEditingId(id);
    setModalOpen(true);
    setFormLoading(true);
    try {
      const data = await endPoints.getService(id);
      setForm({
        name: data.name || '',
        description: data.description || '',
        price: data.price || '',
        hora_inicio: data.hora_inicio || '',
        hora_fin: data.hora_fin || '',
        duration: data.duration || '',
        active: data.active !== false,
      });
      setImage(null);
    } catch (_err) {
      showNotification('Error obteniendo servicio', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCreate = () => navigate('/admin/createService');

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleImageChange = e => {
    if (e.target.files && e.target.files[0]) setImage(e.target.files[0]);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const data = { ...form };
      if (image) data.image = image;
      await endPoints.updateService(editingId, data);
      fetchServices();
      setModalOpen(false);
      setEditingId(null);
      showNotification('Servicio actualizado', 'success');
    } catch (_err) {
      showNotification('Error actualizando servicio', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  if (loading) return <Loader />;

  const columns = [
    { id: 'name', label: 'Nombre' },
    { id: 'price', label: 'Precio' },
    { id: 'activeLabel', label: 'Activo' },
  ];

  const actions = service => (
    <>
      <IconButton onClick={() => handleEdit(service.id)} sx={{ color: ACTION_ICON_COLORS.edit }}>
        <EditIcon />
      </IconButton>
      <IconButton onClick={() => handleDelete(service.id)} sx={{ color: ACTION_ICON_COLORS.delete }}>
        <DeleteIcon />
      </IconButton>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Gestión de Servicios | CES</title>
      </Helmet>
      <Container maxWidth="xl" sx={INTRANET_PAGE_CONTAINER_SX}>
        <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:2, flexWrap:'wrap' }}>
          <PageTitle>Gestión de Servicios</PageTitle>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>Crear Servicio</Button>
        </Box>
        <DataTable columns={columns} rows={services.map(s => ({ ...s, activeLabel: s.active === false ? 'No' : 'Sí' }))} actions={actions} />
        <Modal
        open={modalOpen}
        onClose={handleClose}
        slotProps={{ backdrop:{ onClick: handleClose } }}
      >
        <Card onClick={e => e.stopPropagation()} sx={{ p:2, m:'5% auto', width:{ xs:'90%', sm:400 } }}>
          <Typography variant='h6' sx={{ textAlign:'center', p:2 }}>Editar Servicio</Typography>
          {formLoading ? (
            <Loader />
          ) : (
            <Box component='form' onSubmit={handleSubmit} sx={{ display:'flex', flexDirection:'column', gap:2 }}>
              <TextField label='Nombre' name='name' value={form.name} onChange={handleChange} required />
              <TextField label='Descripción' name='description' value={form.description} onChange={handleChange} multiline rows={2} />
              <TextField label='Precio' name='price' type='number' value={form.price} onChange={handleChange} />
              <TextField label='Hora Inicio' name='hora_inicio' type='time' value={form.hora_inicio} onChange={handleChange} />
              <TextField label='Hora Fin' name='hora_fin' type='time' value={form.hora_fin} onChange={handleChange} />
              <TextField label='Duración (min)' name='duration' type='number' value={form.duration} onChange={handleChange} />
              <Input type='file' accept='image/*' onChange={handleImageChange} />
              <FormControlLabel
                control={<Checkbox name='active' checked={Boolean(form.active)} onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))} />}
                label='Activo (visible en Pide tu cita)'
              />
              <Box sx={{ display:'flex', gap:2, justifyContent:'center' }}>
                <Button onClick={handleClose}>Cancelar</Button>
                <Button type='submit' variant='contained'>Actualizar</Button>
              </Box>
            </Box>
          )}
        </Card>
      </Modal>
      </Container>
    </>
  );
};

export default ManageServices;

